import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { crmApi, leadsApi, newsletterApi } from '../api/leadCaptureApi';
import {
  LeadCreateDto,
  LeadListQuery,
  LeadNotePayload,
  UpdateLeadStatusDto,
} from '../types/leadCapture';

export const useLeads = (params: LeadListQuery = {}) =>
  useQuery({
    queryKey: ['leads', params],
    queryFn: () => leadsApi.getLeads(params),
    staleTime: 30000,
  });

export const useLead = (leadId: string) =>
  useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => leadsApi.getLead(leadId),
    enabled: !!leadId,
    staleTime: 30000,
  });

export const useLeadTimeline = (leadId: string) =>
  useQuery({
    queryKey: ['lead-timeline', leadId],
    queryFn: () => leadsApi.getLeadTimeline(leadId),
    enabled: !!leadId,
    staleTime: 30000,
  });

export const useDashboardMetrics = (
  enabled = true
) =>
  useQuery({
    queryKey: ['forms-dashboard'],
    queryFn: () => leadsApi.getDashboardMetrics(),
    staleTime: 30000,
    enabled,
  });

export const useNewsletterSubscribers = () =>
  useQuery({
    queryKey: ['newsletter-subscribers'],
    queryFn: () => newsletterApi.getSubscribers(),
    staleTime: 30000,
  });

export const useCrmProviders = () =>
  useQuery({
    queryKey: ['crm-providers'],
    queryFn: () => crmApi.getProviders(),
    staleTime: 30000,
  });

export const useCreateLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: LeadCreateDto) => leadsApi.createLead(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead-dashboard'] });
    },
  });
};

export const useUpdateLeadStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, payload }: { leadId: string; payload: UpdateLeadStatusDto }) =>
      leadsApi.updateLeadStatus(leadId, payload),
    onSuccess: (_, { leadId }) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      queryClient.invalidateQueries({ queryKey: ['lead-timeline', leadId] });
    },
  });
};

export const useAddLeadNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, payload }: { leadId: string; payload: LeadNotePayload }) => leadsApi.addLeadNote(leadId, payload),
    onSuccess: (_, { leadId }) => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      queryClient.invalidateQueries({ queryKey: ['lead-timeline', leadId] });
    },
  });
};

export const useSubscribeNewsletter = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { email: string; firstName: string }) => newsletterApi.subscribe(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletter-subscribers'] });
    },
  });
};
